import {useContext} from 'react';
import {Context} from '../context';
import {
  CART_LINES_ADD_MUTATION,
  CART_CREATE_MUTATION,
  normalizeCart,
} from '../cart-graphql';

// Combine the existing cart lines with the newly requested ones, summing
// quantities per variant, so recreating an expired cart preserves what the
// user already had instead of replacing it with just the latest click.
function mergeLines(existingLines, newLines) {
  const merged = new Map();
  for (const line of existingLines || []) {
    const merchandiseId = line.merchandise?.id;
    if (merchandiseId == null) continue;
    // Existing lines may come from possibly-stale localStorage — guard against a
    // missing/invalid quantity so we don't send NaN to cartCreate and 400 the
    // whole recreate.
    const qty = Number(line.quantity);
    if (!Number.isInteger(qty) || qty < 1) continue;
    merged.set(merchandiseId, (merged.get(merchandiseId) || 0) + qty);
  }
  for (const line of newLines) {
    merged.set(
      line.merchandiseId,
      (merged.get(line.merchandiseId) || 0) + line.quantity,
    );
  }
  return Array.from(merged.entries()).map(([merchandiseId, quantity]) => ({
    merchandiseId,
    quantity,
  }));
}

export function useAddItemsToCart() {
  const {client, cart, setCart, isInitializing} = useContext(Context);

  async function addItemsToCart(items) {
    if (client == null) {
      throw new Error('Shopify client not initialized');
    }

    if (cart == null || isInitializing) {
      throw new Error('Cart is not ready yet. Please wait for cart to initialize.');
    }

    if (items.length < 1) {
      throw new Error(
        'Must include at least one line item, empty line items found',
      );
    }

    items.forEach((item) => {
      if (item.variantId == null) {
        throw new Error(`Missing variantId in item`);
      }

      if (item.quantity == null) {
        throw new Error(
          `Missing quantity in item with variant id: ${item.variantId}`,
        );
      } else if (typeof item.quantity != 'number') {
        throw new Error(
          `Quantity is not a number in item with variant id: ${item.variantId}`,
        );
      } else if (item.quantity < 1) {
        throw new Error(
          `Quantity must not be less than one in item with variant id: ${item.variantId}`,
        );
      }
    });

    const lines = items.map(item => ({
      merchandiseId: item.variantId,
      quantity: item.quantity
    }));

    try {
      const response = await client.request(CART_LINES_ADD_MUTATION, {
        variables: {
          cartId: cart.id,
          lines: lines
        }
      });

      // `client.request` resolves (does not throw). Only a genuine transport
      // failure — server unreachable or an HTTP error (no `data`, and
      // networkStatusCode missing or not a 2xx) — should surface for retry;
      // recreating then would risk forging a partial cart. A response that
      // reached Shopify (networkStatusCode 200) with a null cart means the cart
      // id is invalid (expired/deleted) and must self-heal, even when it also
      // carries a top-level GraphQL error.
      const status = response?.errors?.networkStatusCode;
      const isTransportFailure =
        !response?.data && (status == null || status < 200 || status >= 300);
      if (isTransportFailure) {
        throw new Error(
          response?.errors?.message || 'Network error adding items to cart',
        );
      }

      const result = response?.data?.cartLinesAdd;
      const updatedCart = result?.cart;
      const userErrors = result?.userErrors;

      // A null cart (the request reached Shopify) means the cart id is invalid —
      // expired or deleted server-side. Recreate it with the existing lines
      // merged in so the user's click and prior items aren't lost.
      if (!updatedCart) {
        console.warn(
          'cartLinesAdd returned null cart (expired), recreating',
          userErrors || response?.errors || '',
        );
        await recreateCartWithLines(mergeLines(cart.lines, lines));
        return;
      }

      // Cart came back present but with errors (e.g. out of stock, quantity
      // capped). Sync local state to whatever the server actually did, then
      // surface the error instead of reporting a false success.
      if (userErrors?.length > 0) {
        setCart(normalizeCart(updatedCart));
        throw new Error(userErrors[0].message);
      }

      setCart(normalizeCart(updatedCart));
    } catch (error) {
      console.error('Error adding items to cart:', error);
      throw error;
    }
  }

  async function recreateCartWithLines(lines) {
    const createResponse = await client.request(
      CART_CREATE_MUTATION,
      {variables: {input: {lines}}},
    );
    const result = createResponse?.data?.cartCreate;
    const newCart = result?.cart;
    if (!newCart) {
      throw new Error(
        result?.userErrors?.[0]?.message || 'Failed to recreate cart',
      );
    }
    // cartCreate can return a cart while silently dropping unavailable lines
    // (e.g. a variant that sold out during the old cart's lifetime), reporting
    // them in userErrors. Sync to what the server actually created, then surface
    // the error so we don't report a false success with a quietly shorter cart.
    setCart(normalizeCart(newCart));
    if (result?.userErrors?.length > 0) {
      throw new Error(result.userErrors[0].message);
    }
  }

  return addItemsToCart;
}
